import React from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, Video } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../contexts/UserProfileContext';
import RecruiterLoggedOutGate from '../RecruiterLoggedOutGate';
import { RecruiterGateCard, RecruiterLoadingScreen, RecruiterPageShell } from '../Recruiter/RecruiterStates';

/**
 * Gate for surfaces that should be visible to any signed-in recruiter.
 * - Not signed in → shared recruiter logged-out gate
 * - Signed in explorer → recruiter-only hint
 * - Signed in recruiter (or admin) → render children
 */
interface Props {
  children: React.ReactNode;
  // Icon for the auth/profile loading screen. Pass the page's own icon so the
  // guard→page transition shows one continuous loader instead of two flashes.
  loadingIcon?: React.ComponentType<{ className?: string }>;
  /**
   * Optional override for the signed-out state (e.g. passport share deep-links
   * want a purpose-specific gate instead of the generic hiring pitch).
   */
  loggedOutFallback?: React.ReactNode;
}

const RecruiterGuard: React.FC<Props> = ({ children, loadingIcon = Video, loggedOutFallback }) => {
  const { isLoading: authLoading, isSignedIn, user } = useAuth();
  const { userProfile } = useUserProfile();
  const location = useLocation();
  const profileLoading = isSignedIn && !!user?.userId && (!userProfile || userProfile.id !== user.userId);

  if (authLoading || profileLoading) {
    return <RecruiterLoadingScreen icon={loadingIcon} />;
  }

  if (!isSignedIn) {
    if (loggedOutFallback) return <>{loggedOutFallback}</>;
    return <RecruiterLoggedOutGate returnTo={location.pathname + location.search} />;
  }

  const isRecruiter = userProfile?.is_recruiter === true || userProfile?.is_admin === true;
  if (!isRecruiter) {
    return (
      <RecruiterPageShell noPadding contentClassName="flex min-h-screen items-center justify-center px-4 py-12">
        <RecruiterGateCard
          icon={<Building2 className="size-6 text-white" />}
          title="Recruiter access only"
          description="Sign in with a recruiter account to use this feature."
        >
          <a
            href="/"
            className="mt-8 flex w-full items-center justify-center rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
          >
            Go Home
          </a>
        </RecruiterGateCard>
      </RecruiterPageShell>
    );
  }

  return <>{children}</>;
};

export default RecruiterGuard;
